import {useCallback, useState} from "react";

import {generateStrongPassword} from "../util/passwordGenerator";

const MAX_GENERATE_ATTEMPTS = 10;

/**
 * #19351: State of the "new password + repeat" fields shared by the profile dialog and the reset page: typing
 * hides a previously generated password, generating fills both fields and shows it once for copying.
 * Without `requireRepeat` (confirmed e-mail address: a mistyped password is recoverable via "Passwort vergessen")
 * the repeat field is not shown and never counts as a mismatch.
 */
export function useNewPasswordFields(requireRepeat = true) {
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordRepeat, setNewPasswordRepeat] = useState("");
    const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    const passwordMismatch =
        requireRepeat && newPassword !== "" && newPasswordRepeat !== "" && newPassword !== newPasswordRepeat;

    const changeNewPassword = (value: string) => {
        setNewPassword(value);
        setGeneratedPassword(null);
    };

    const changeNewPasswordRepeat = (value: string) => {
        setNewPasswordRepeat(value);
        setGeneratedPassword(null);
    };

    /**
     * `isAcceptable` lets a caller reject a suggestion its own rules would refuse (e.g. a random run of letters that
     * happens to be part of the user's name); like the server's generator it retries a few times.
     */
    const generatePassword = (isAcceptable?: (password: string) => boolean) => {
        let generated = generateStrongPassword();
        for (let attempt = 1; isAcceptable && !isAcceptable(generated) && attempt < MAX_GENERATE_ATTEMPTS; attempt++) {
            generated = generateStrongPassword();
        }
        setNewPassword(generated);
        setNewPasswordRepeat(generated);
        setGeneratedPassword(generated);
        setCopied(false);
    };

    const copyGeneratedPassword = () => {
        navigator.clipboard
            ?.writeText(generatedPassword ?? "")
            .then(() => setCopied(true))
            .catch(() => {
                // Clipboard access denied/unavailable — the password is still shown on screen to copy by hand.
            });
    };

    const reset = useCallback(() => {
        setNewPassword("");
        setNewPasswordRepeat("");
        setGeneratedPassword(null);
        setCopied(false);
    }, []);

    return {
        requireRepeat,
        newPassword,
        newPasswordRepeat,
        generatedPassword,
        copied,
        passwordMismatch,
        changeNewPassword,
        changeNewPasswordRepeat,
        generatePassword,
        copyGeneratedPassword,
        reset,
    };
}
